import { useState } from "react";
import { newsCategories, type NewsItem } from "@/mocks/news";

export type TimeRange = "all" | "week" | "month" | "quarter";
export type SortKey = "latest" | "hot";

export interface NewsFilterValue {
  time: TimeRange;
  category: "全部" | NewsItem["category"];
  sort: SortKey;
}

interface NewsFiltersProps {
  value: NewsFilterValue;
  onChange: (value: NewsFilterValue) => void;
}

const timeOptions: { key: TimeRange; label: string }[] = [
  { key: "all", label: "全部时间" },
  { key: "week", label: "近一周" },
  { key: "month", label: "近一月" },
  { key: "quarter", label: "近三月" },
];

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "latest", label: "最新发布" },
  { key: "hot", label: "按热度" },
];

export default function NewsFilters({ value, onChange }: NewsFiltersProps) {
  const [open, setOpen] = useState(false);
  const categoryOptions: ("全部" | NewsItem["category"])[] = ["全部", ...newsCategories];

  const chipClass = (active: boolean) =>
    `whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors cursor-pointer ${
      active
        ? "bg-primary-500 font-semibold text-background-50"
        : "bg-background-100 text-foreground-700 hover:bg-background-200"
    }`;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between md:hidden cursor-pointer"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-filter-3-line text-base text-primary-600" aria-hidden="true"></i>
          筛选与排序
        </span>
        <i className={open ? "ri-arrow-up-s-line text-xl text-foreground-500" : "ri-arrow-down-s-line text-xl text-foreground-500"} aria-hidden="true"></i>
      </button>

      <div className={`${open ? "mt-4 flex" : "hidden"} flex-col gap-4 md:mt-0 md:flex`}>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <span className="w-20 shrink-0 text-sm font-medium text-foreground-500">时间范围</span>
          <div className="flex flex-wrap gap-2">
            {timeOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onChange({ ...value, time: opt.key })}
                className={chipClass(value.time === opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-background-200 pt-4 md:flex-row md:items-center">
          <span className="w-20 shrink-0 text-sm font-medium text-foreground-500">新闻类型</span>
          <div className="flex flex-wrap gap-2">
            {categoryOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onChange({ ...value, category: opt })}
                className={chipClass(value.category === opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-background-200 pt-4 md:flex-row md:items-center">
          <span className="w-20 shrink-0 text-sm font-medium text-foreground-500">排序方式</span>
          <div className="flex flex-wrap gap-2">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onChange({ ...value, sort: opt.key })}
                className={chipClass(value.sort === opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}